'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Program {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  targetAudience: string;
  keyBenefits: string[];
  color: string;
}

interface ProgramCardProps {
  program: Program;
  isHydrated: boolean;
}

const ProgramCard = ({ program, isHydrated }: ProgramCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative bg-card rounded-2xl overflow-hidden shadow-card hover:shadow-modal transition-all duration-500 transform hover:-translate-y-2"
      onMouseEnter={() => isHydrated && setIsHovered(true)}
      onMouseLeave={() => isHydrated && setIsHovered(false)}>

      <div className="relative h-64 overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10 transition-opacity duration-500 ${isHovered ? 'opacity-90' : 'opacity-60'}`} />
        <AppImage
          src={program.image}
          alt={program.alt}
          className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`} />

        <div className="absolute top-4 right-4 z-20">
          <div className={`w-12 h-12 ${program.color} rounded-full flex items-center justify-center shadow-hover`}>
            <Icon name={program.icon as any} size={24} className="text-white" variant="solid" />
          </div>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-2xl font-heading font-bold text-primary mb-3 group-hover:text-accent transition-colors duration-300">
          {program.title}
        </h3>
        <p className="text-sm text-accent font-heading font-semibold mb-3 uppercase tracking-wider">
          {program.targetAudience}
        </p>
        <p className="text-base text-text-secondary font-body mb-4 leading-relaxed">
          {program.description}
        </p>

        <div className="space-y-2 mb-6">
          {program.keyBenefits.map((benefit, index) =>
          <div key={index} className="flex items-start gap-2">
              <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
              <p className="text-sm text-text-primary font-body">{benefit}</p>
            </div>
          )}
        </div>

        <Link
          href="/programs-overview"
          className="inline-flex items-center gap-2 text-accent font-heading font-bold text-base hover:gap-3 transition-all duration-300">

          Learn More
          <Icon name="ArrowRightIcon" size={20} />
        </Link>
      </div>
    </div>);

};

const ProgramCards = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const programs: Program[] = [
  {
    id: 1,
    title: 'Speed & Strength Training',
    description: 'Comprehensive athletic development program merging elite speed training with strength and conditioning. Build explosive power, improve acceleration, and develop functional strength through Olympic methodologies and cutting-edge biomechanics.',
    image: "/assets/images/PHOTO-2024-11-16-20-20-02-1770454700441.jpg",
    alt: 'Athletic young man in black sportswear sprinting on outdoor track with intense focus and proper running form',
    icon: 'BoltIcon',
    targetAudience: 'Youth & Teens (10+) | Intermediate to Advanced',
    keyBenefits: [
    'Olympic-level sprint mechanics training',
    'Plyometric power development',
    'Strength and conditioning integration'],

    color: 'bg-theme-orange'
  },
  {
    id: 2,
    title: 'Go Speed Field Performance Track',
    description: 'Elite technical events training program specializing in throws, jumps, and pole vault. Master the biomechanics of javelin, shot put, discus, long jump, high jump, and pole vault under the guidance of national champions with proven competition success.',
    image: "https://images.unsplash.com/photo-1528474495462-b16cd84a0cf0",
    alt: 'Athlete performing pole vault technique with proper form against blue sky, demonstrating technical field event mastery',
    icon: 'FireIcon',
    targetAudience: 'Youth & Adults (10+) | Intermediate to Advanced',
    keyBenefits: [
    'Specialized throws training (javelin, shot put, discus)',
    'Jumps technique development (long jump, high jump)',
    'Pole vault technical mastery'],

    color: 'bg-theme-red'
  },
  {
    id: 3,
    title: 'General Fitness Program',
    description: 'Holistic wellness program combining cardiovascular conditioning, strength training, and flexibility work. Designed for sustainable health improvements and lifelong fitness habits with balanced, enjoyable workouts.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b83293f3-1766376869491.png",
    alt: 'Diverse group of adults in colorful workout attire doing yoga poses on mats in bright studio with natural lighting',
    icon: 'HeartIcon',
    targetAudience: 'Adults (18-54) | Beginner to Intermediate',
    keyBenefits: [
    'Balanced cardio and strength training',
    'Flexibility and mobility sessions',
    'Nutrition guidance and meal planning'],

    color: 'bg-theme-orange'
  },
  {
    id: 4,
    title: 'Kids Foundation Program',
    description: 'Age-appropriate athletic development program building fundamental movement skills, coordination, and confidence. Fun, engaging sessions that instill lifelong fitness habits while developing physical literacy and social skills.',
    image: "/assets/images/tempImagesUMsCM-2-3-1770454557293.jpg",
    alt: 'Young athletes with coach at medal ceremony, wearing medals and holding trophies after successful competition at stadium',
    icon: 'SparklesIcon',
    targetAudience: 'Kids (6-10) | Beginner',
    keyBenefits: [
    'Fundamental movement skill development',
    'Age-appropriate strength and conditioning',
    'Coordination and balance training'],

    color: 'bg-theme-black'
  }];


  return (
    <section className="w-full py-16 lg:py-24 bg-muted">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-6">
            <p className="text-accent font-heading font-bold text-sm uppercase tracking-wider">
              Our Programs
            </p>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-6 leading-tight">
            Pathways to Your Fitness Goals
          </h2>
          <p className="text-lg text-text-secondary font-body max-w-3xl mx-auto leading-relaxed">
            From youth development to elite athletic training, discover structured programs designed for every age, goal, and fitness level.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {programs.map((program) =>
          <ProgramCard key={program.id} program={program} isHydrated={isHydrated} />
          )}
        </div>

        <div className="text-center mt-12">
          <Link
            href="/programs-overview"
            className="inline-flex items-center gap-2 px-8 py-4 bg-theme-black text-white font-heading font-bold text-lg rounded-md shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">

            View All Programs
            <Icon name="ArrowRightIcon" size={24} />
          </Link>
        </div>
      </div>
    </section>);

};

export default ProgramCards;